// src/components/chat/ChatHeader.tsx
import React from 'react';
import styled from 'styled-components';

interface ChatHeaderProps {
  title?: string;
  subtitle?: string;
  logoUrl?: string;
  backgroundColor?: string;
  textColor?: string;
  showPulse?: boolean;
  customStyles?: React.CSSProperties;
  onClose?: () => void;
}

const HeaderContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'backgroundColor'
})<{ backgroundColor?: string }>`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: ${props => props.backgroundColor || '#ffffff'};
  border-bottom: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    padding: 0.625rem 0.875rem;
    min-height: 56px;
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const Logo = styled.h1.withConfig({
  shouldForwardProp: (prop) => !['textColor', 'showPulse'].includes(prop)
})<{ textColor?: string; showPulse?: boolean }>`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  letter-spacing: -0.5px;
  
  ${props => props.textColor ? `
    color: ${props.textColor};
  ` : `
    color: transparent;
    background: linear-gradient(135deg, #4A55A2 0%, #7B66FF 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  `}
  
  &::before {
    content: ${props => props.showPulse ? "'●'" : "''"};
    color: #4A55A2;
    margin-right: 6px;
    font-size: 0.7em;
    vertical-align: middle;
    animation: ${props => props.showPulse ? 'pulse 2s infinite' : 'none'};
  }

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const SubTitle = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'textColor'
})<{ textColor?: string }>`
  font-size: 0.8rem;
  color: ${props => props.textColor || '#666'};
  margin-left: 6px;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-left: 4px;
  }
`;

const LogoImage = styled.img`
  width: 28px;
  height: 28px;
  margin-right: 6px;
  border-radius: 4px;
  
  &:error {
    display: none;
  }
  
  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    margin-right: 4px;
  }
`;

const CloseButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'textColor'
})<{ textColor?: string }>`
  background: none;
  border: none;
  color: ${props => props.textColor || '#666'};
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  min-width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    opacity: 0.8;
  }
  
  @media (max-width: 768px) {
    min-width: 48px;
    height: 48px;
    font-size: 28px;
    padding: 10px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.05);
    
    &:active {
      background: rgba(0, 0, 0, 0.1);
      transform: scale(0.95);
    }
  }
`;

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  title = 'Nexly Chat',
  subtitle = 'AI Assistant',
  logoUrl,
  backgroundColor,
  textColor,
  showPulse = true,
  customStyles,
  onClose
}) => {
  return (
    <HeaderContainer 
      backgroundColor={backgroundColor}
      style={customStyles}
    >
      <HeaderTitle>
        {logoUrl && (
          <LogoImage 
            src={logoUrl} 
            alt="Logo" 
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        <Logo textColor={textColor} showPulse={showPulse && !logoUrl}>
          {title}
        </Logo>
        {subtitle && <SubTitle textColor={textColor}>{subtitle}</SubTitle>}
      </HeaderTitle>
      {onClose && (
        <CloseButton onClick={onClose} textColor={textColor}>
          ×
        </CloseButton>
      )}
    </HeaderContainer>
  );
};
